import * as React from 'react';
import { useEffect } from 'react';
import { useCollectionState, setInteractionModality, useId, useItem, useListBoxSection } from '@sail/react-aria';
import { assignProps, view, createViewConfig } from '@sail/engine';
import { chevronDown } from '@sail/icons/react/Icon';
import { Header } from './Header';
import { Icon } from './Icon';
import { css } from '../css';
export const ListBoxGroupTransforms = {
    idField: 'id',
    makeGenericItem (props) {
        var _props_textValue;
        return {
            type: 'section',
            'aria-label': props['aria-label'],
            textValue: typeof props.title === 'string' ? props.title : (_props_textValue = props.textValue) !== null && _props_textValue !== void 0 ? _props_textValue : ''
        };
    }
};
export const ListBoxGroupConfig = createViewConfig({
    name: 'ListBoxGroup',
    props: {}
});
export const ListBoxGroup = ListBoxGroupConfig.createView(function ListBoxGroup({ id, children, title, subviews, ...props }) {
    const headerRef = React.useRef(null);
    const key = useItem(id, ListBoxGroupTransforms.makeGenericItem({
        'aria-label': props['aria-label'],
        title
    }));
    const { itemProps, headingProps: { id: headingId }, groupProps } = useListBoxSection({
        heading: title,
        'aria-label': props['aria-label']
    });
    const state = useCollectionState();
    const tree = state;
    const isOpen = !tree.expandable || tree.expandedKeys.has(key);
    const groupId = useId();
    const triggerProps = {
        role: 'button',
        'aria-expanded': isOpen,
        'aria-controls': groupId
    };
    const focusedItem = state.collection.getItem(state.selectionManager.focusedKey);
    useEffect(()=>{
        if (focusedItem) {
            // expand all parents of the focused item
            let { parentKey } = focusedItem;
            const keysToExpand = new Set();
            while(parentKey){
                keysToExpand.add(parentKey);
                const parent = tree.collection.getItem(parentKey);
                parentKey = parentKey === (parent === null || parent === void 0 ? void 0 : parent.parentKey) ? undefined : parent === null || parent === void 0 ? void 0 : parent.parentKey;
            }
            if (Array.from(keysToExpand).some((key)=>!tree.expandedKeys.has(key))) {
                tree.setExpandedKeys(new Set([
                    ...keysToExpand,
                    ...tree.expandedKeys
                ]));
                setInteractionModality('keyboard');
            }
        }
    }, [
        tree,
        focusedItem
    ]);
    return /*#__PURE__*/ React.createElement(view.div, {
        ...props,
        ...itemProps
    }, title ? /*#__PURE__*/ React.createElement(Header, {
        id: headingId,
        role: "presentation",
        inherits: subviews.header,
        onClick: ()=>{
            if (tree.expandable) {
                tree.selectionManager.setFocusedKey(null);
                tree.toggleKey(key);
            }
        },
        onMouseDown: (e)=>{
            var _headerRef_current;
            // Prevent focus going to the listbox when clicking the section header.
            if ((_headerRef_current = headerRef.current) === null || _headerRef_current === void 0 ? void 0 : _headerRef_current.contains(e.target)) {
                e.preventDefault();
            }
        },
        uses: [
            assignProps(tree.expandable ? triggerProps : {})
        ],
        css: !tree.expandable ? css({
            cursor: 'default'
        }) : css({
            cursor: 'pointer'
        }),
        ref: headerRef
    }, tree.expandable ? /*#__PURE__*/ React.createElement(Icon, {
        size: "xsmall",
        icon: chevronDown,
        css: [
            css({
                transition: 'transform 150ms',
                marginX: 'xxsmall'
            }),
            isOpen ? css({
                transform: 'rotate(0deg)'
            }) : css({
                transform: 'rotate(-90deg)'
            })
        ]
    }) : null, title) : null, /*#__PURE__*/ React.createElement("section", {
        role: "region",
        id: id,
        ...groupProps
    }, isOpen ? children : null));
}, {
    css: {
        padding: 'xsmall',
        bleedX: 'xsmall',
        borderWidth: '0',
        borderTopWidth: 'small',
        borderStyle: 'solid',
        borderColor: 'border',
        ':first-of-type': {
            borderTopWidth: '0',
            bleedTop: 'xsmall'
        },
        ':last-of-type': {
            bleedBottom: 'xsmall'
        }
    },
    subviews: {
        header: {
            css: {
                stack: 'x',
                gap: 'space.75',
                distribute: 'packed',
                font: 'heading.xsmall',
                color: 'form',
                paddingY: 'space.75',
                paddingX: 'space.125',
                userSelect: 'none'
            }
        },
        list: {
            css: {
                stack: 'y'
            }
        }
    }
});

//# sourceMappingURL=ListBoxGroup.js.map